// DSC helpers shared by the list page and the add/edit modal. Mirrors the desk
// utility this replaces: validity status is DERIVED from `validTo`, DSC codes
// sort "smartly" (numeric segments numerically), and PAN is format-checked.

export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export const isValidPan = (pan) => PAN_REGEX.test((pan || '').trim().toUpperCase());

// Window (in days) before expiry within which a DSC counts as "expiring soon".
export const EXPIRING_WINDOW_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Whole-day difference between `validTo` and today (both floored to midnight),
// so a DSC that lapses later today reads as 0 days, not a fractional value.
export function daysUntil(validTo) {
  if (!validTo) return null;
  const till = new Date(validTo);
  if (Number.isNaN(till.getTime())) return null;
  const a = new Date(till.getFullYear(), till.getMonth(), till.getDate());
  const now = new Date();
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / MS_PER_DAY);
}

// active | expiring | expired | unknown (no valid-to on the record).
export function dscStatus(validTo) {
  const days = daysUntil(validTo);
  if (days === null) return 'unknown';
  if (days < 0) return 'expired';
  if (days <= EXPIRING_WINDOW_DAYS) return 'expiring';
  return 'active';
}

// Design-system StatusPill variant per status.
export const STATUS_VARIANT = {
  active: 'success',
  expiring: 'warning',
  expired: 'danger',
  unknown: 'neutral',
};

export const STATUS_LABEL = {
  active: 'Active',
  expiring: 'Expiring',
  expired: 'Expired',
  unknown: 'No date',
};

// Human "12 days left" / "expired 3 days ago" / "expires today".
export function validityText(validTo) {
  const days = daysUntil(validTo);
  if (days === null) return '—';
  if (days === 0) return 'Expires today';
  if (days > 0) return `${days} day${days === 1 ? '' : 's'} left`;
  const ago = -days;
  return `Expired ${ago} day${ago === 1 ? '' : 's'} ago`;
}

// Custody: which side physically holds the token right now.
export const HOLDER_LABEL = { firm: 'With firm', client: 'With client' };
export const HOLDER_VARIANT = { firm: 'success', client: 'info' };

export const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'expiring', label: 'Expiring' },
  { value: 'expired', label: 'Expired' },
  { value: 'unknown', label: 'No date' },
];

// Smart DSC-code comparator: split each code into alternating text / number
// segments and compare segment-by-segment — numeric segments numerically so
// "DSC-2" sorts before "DSC-10", text segments alphabetically. Blank codes
// sort last.
function tokenizeCode(code) {
  return String(code || '')
    .toLowerCase()
    .match(/(\d+|\D+)/g) || [];
}

export function compareDscCode(a, b) {
  const ca = (a || '').trim();
  const cb = (b || '').trim();
  if (!ca && !cb) return 0;
  if (!ca) return 1; // blanks last
  if (!cb) return -1;

  const ta = tokenizeCode(ca);
  const tb = tokenizeCode(cb);
  const len = Math.max(ta.length, tb.length);
  for (let i = 0; i < len; i += 1) {
    const sa = ta[i];
    const sb = tb[i];
    if (sa === undefined) return -1; // shorter code first
    if (sb === undefined) return 1;
    const na = Number(sa);
    const nb = Number(sb);
    const bothNum = !Number.isNaN(na) && !Number.isNaN(nb) && /\d/.test(sa) && /\d/.test(sb);
    if (bothNum) {
      if (na !== nb) return na - nb;
    } else if (sa !== sb) {
      return sa < sb ? -1 : 1;
    }
  }
  return 0;
}

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── CSV export / import ──────────────────────────────────────────────────────
// The columns the desk utility used, in order. Kept as one source of truth so
// export headers and import parsing stay in step.
export const CSV_COLUMNS = [
  { key: 'dscCode', header: 'DSC Code' },
  { key: 'name', header: 'Name' },
  { key: 'clientName', header: 'Client' },
  { key: 'pan', header: 'PAN' },
  { key: 'tokenSerial', header: 'Token Serial' },
  { key: 'validFrom', header: 'Valid From' },
  { key: 'validTo', header: 'Valid To' },
  { key: 'associatedPerson', header: 'Associated Person' },
  { key: 'place', header: 'Place' },
  // Export-only summary of the custody log ("With firm — Ramesh since
  // 12-Mar-2026"); the import path ignores it, since custody is written only
  // through recorded handovers, never a spreadsheet cell.
  { key: 'custody', header: 'Custody' },
  { key: 'remarks', header: 'Remarks' },
];

export function custodyText(d) {
  const label = HOLDER_LABEL[d.currentHolder] || 'With firm';
  const bits = [label];
  if (d.currentHolderName) bits.push(d.currentHolderName);
  if (d.custodySince) bits.push(`since ${fmtDate(d.custodySince)}`);
  return bits.join(' — ');
}

const toDateOnly = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const csvCell = (value) => {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function toCsv(rows) {
  const header = CSV_COLUMNS.map((c) => csvCell(c.header)).join(',');
  const body = rows.map((r) =>
    CSV_COLUMNS.map((c) => {
      const v = c.key === 'validFrom' || c.key === 'validTo' ? toDateOnly(r[c.key]) : r[c.key];
      return csvCell(v);
    }).join(',')
  );
  return [header, ...body].join('\n');
}


// ⚠️ The CSV *parser* that used to live here is gone. Importing a register in
// the browser meant no template, no dropdowns and a result that could only say
// "3 added, 1 failed" — the server now parses an .xlsx and reports every failed
// row with its number and reason (see dscImportController). Exporting stays
// here: reading a register out to Excel has no such problem.
